/**
 * Prompt & Model Governance Types
 * 
 * Type definitions for the Admin UI prompt library and model registry.
 * These support versioned prompts and allowlisted models with provenance tracking.
 */

/**
 * Prompt status in the lifecycle
 */
export enum PromptStatus {
  Draft = 'draft',
  Approved = 'approved',
  Deprecated = 'deprecated'
}

/**
 * LLM provider
 */
export enum ModelProvider {
  OpenAI = 'openai',
  Gemini = 'gemini'
}

/**
 * Prompt version entry in the library
 */
export interface PromptVersion {
  /** Unique prompt ID */
  id: string;
  
  /** Human-readable title */
  title: string;
  
  /** Target language */
  language: 'tr' | 'de' | 'en';
  
  /** Target CEFR level */
  level: 'B2' | 'B1';
  
  /** Prompt body (the actual prompt text) */
  body: string;
  
  /** Tags for categorization */
  tags: string[];
  
  /** Lifecycle status */
  status: PromptStatus;
  
  /** User who created this prompt */
  created_by: string;
  
  /** Creation timestamp */
  created_at: string;
  
  /** User who approved this prompt */
  approved_by?: string;
  
  /** Approval timestamp */
  approved_at?: string;
}

/**
 * Model configuration in the registry
 */
export interface ModelConfig {
  /** Unique model ID (format: provider:modelName) */
  id: string;
  
  /** LLM provider */
  provider: ModelProvider;
  
  /** Model parameters (temperature, maxTokens, etc.) */
  parameters: {
    temperature?: number;
    maxTokens?: number;
    [key: string]: unknown;
  };
  
  /** Estimated cost per 1k tokens (USD) */
  estimated_cost_per_1k_tokens?: number;
  
  /** Whether this model is enabled for use */
  enabled: boolean;
  
  /** Creation timestamp */
  created_at?: string;
  
  /** Last update timestamp */
  updated_at?: string;
}

/**
 * Filters for prompt queries
 */
export interface PromptsFilters {
  language?: 'tr' | 'de' | 'en';
  level?: 'B2' | 'B1';
  status?: PromptStatus;
  tags?: string[];
}

/**
 * Filters for model queries
 */
export interface ModelsFilters {
  provider?: ModelProvider;
  enabled?: boolean;
}

/**
 * Response for list prompts API
 */
export interface ListPromptsResponse {
  prompts: PromptVersion[];
  total: number;
  page: number;
  perPage: number;
  hasMore: boolean;
}

/**
 * Response for list models API
 */
export interface ListModelsResponse {
  models: ModelConfig[];
  total: number;
}
